/**
 * Unified Blocker Model
 * Structured blockers with type, severity, source, and timestamp
 * that work across Leads, Quotes, and Jobs.
 */

import type { Job, Issue } from "./types";
import type { Quote } from "./quoteTypes";
import type { Lead } from "./pipelineTypes";
import type { ServiceTicket } from "./serviceTicketTypes";
import { isInRescissionFromWonAt } from "./rescission";
import { migrationBypasses } from "./import/migrationVisibilityGuard";

export type BlockerSeverity = "hard" | "warning" | "info";
export type BlockerSource = "system" | "validation" | "user" | "integration";
export type BlockerCategory =
  | "contract" | "permit" | "materials" | "issues" | "qa"
  | "contractor" | "dispatch" | "rescission" | "financial"
  | "compliance" | "data" | "scheduling";

export interface UnifiedBlocker {
  id: string;
  label: string;
  detail: string;
  severity: BlockerSeverity;
  category: BlockerCategory;
  source: BlockerSource;
  entityType: "lead" | "quote" | "job" | "service_ticket";
  entityId: string;
  createdAt: string;
  resolvedAt?: string;
  actionLink?: string;
}

/* ═══════════════════════════════════════════
   LEAD BLOCKERS
   ═══════════════════════════════════════════ */

export function getLeadBlockers(lead: Lead): UnifiedBlocker[] {
  const blockers: UnifiedBlocker[] = [];
  const now = new Date().toISOString();
  const base = { entityType: "lead" as const, entityId: lead.id, source: "system" as const, createdAt: now };

  if (!lead.primaryName.trim())
    blockers.push({ ...base, id: `${lead.id}-name`, label: "Missing Contact Name", detail: "Lead has no primary name", severity: "hard", category: "data" });
  if (!lead.primaryPhone.trim() && !lead.primaryEmail.trim())
    blockers.push({ ...base, id: `${lead.id}-contact`, label: "No Contact Method", detail: "Lead has no phone or email", severity: "hard", category: "data" });
  if (!lead.address.trim())
    blockers.push({ ...base, id: `${lead.id}-addr`, label: "Missing Address", detail: "No address provided", severity: "warning", category: "data" });
  if (!lead.assignedRep)
    blockers.push({ ...base, id: `${lead.id}-rep`, label: "Unassigned", detail: "No sales rep assigned to this lead", severity: "warning", category: "dispatch" });
  if (lead.stage === "Lead") {
    const daysSince = Math.floor((Date.now() - new Date(lead.createdAt).getTime()) / 86400000);
    if (daysSince > 2)
      blockers.push({ ...base, id: `${lead.id}-stale`, label: "Stale Lead", detail: `New lead untouched for ${daysSince} days`, severity: "warning", category: "scheduling" });
  }
  return blockers;
}

/* ═══════════════════════════════════════════
   QUOTE BLOCKERS
   ═══════════════════════════════════════════ */

export function getQuoteBlockers(quote: Quote): UnifiedBlocker[] {
  const blockers: UnifiedBlocker[] = [];
  const now = new Date().toISOString();
  const base = { entityType: "quote" as const, entityId: quote.id, source: "system" as const, createdAt: now };
  const q = quote as Quote & {
    primaryName?: string;
    customerName?: string;
    customer_name?: string;
    primaryPhone?: string;
    customerPhone?: string;
    phone?: string;
  };
  const primaryName = String(q.primaryName ?? q.customerName ?? q.customer_name ?? "").trim();
  const phoneCandidate = q.primaryPhone ?? q.customerPhone ?? q.phone;
  const hasPhoneField = typeof phoneCandidate !== "undefined";
  const primaryPhone = String(phoneCandidate ?? "").trim();
  const cfg = quote.projectConfig;
  const hasProjectConfiguration = Boolean(
    cfg && (
      cfg.selectedVerticals?.length ||
      cfg.bathroomScopes?.length ||
      cfg.bathroomMaterials?.length ||
      cfg.selectedWallSystem ||
      cfg.selectedBase ||
      cfg.selectedGlass ||
      cfg.selectedDoor ||
      cfg.selectedFixture ||
      cfg.selectedGrabBars?.length ||
      cfg.selectedAddOns?.length ||
      cfg.customQuoteItems?.length ||
      cfg.workDescription?.trim() ||
      cfg.autoProjectType?.trim() ||
      cfg.baseWidth?.trim() ||
      cfg.baseLength?.trim()
    )
  );

  // Contract required before conversion
  if (quote.stage === "Appointment Completed" && (!quote.contract || quote.contract.status !== "Signed"))
    blockers.push({ ...base, id: `${quote.id}-contract`, label: "Contract Not Signed", detail: "Contract must be signed before marking as Won", severity: "hard", category: "contract" });

  // Missing customer info
  if (!primaryName)
    blockers.push({ ...base, id: `${quote.id}-name`, label: "Missing Customer Name", detail: "Customer name is required", severity: "hard", category: "data" });
  if (hasPhoneField && !primaryPhone)
    blockers.push({ ...base, id: `${quote.id}-phone`, label: "Missing Phone", detail: "Customer phone is required", severity: "hard", category: "data" });

  // Verification incomplete
  if (quote.verification && !quote.verification.verificationComplete)
    blockers.push({ ...base, id: `${quote.id}-verify`, label: "Verification Incomplete", detail: "Customer & property verification not finished", severity: "warning", category: "compliance" });

  // POA required
  if (quote.verification?.ownershipType === "POA" && !quote.verification?.poaDocumentUploaded)
    blockers.push({ ...base, id: `${quote.id}-poa`, label: "POA Document Missing", detail: "Power of Attorney document must be uploaded", severity: "hard", category: "compliance" });

  // HOA not confirmed
  if (quote.verification?.hoaInvolved === null)
    blockers.push({ ...base, id: `${quote.id}-hoa`, label: "HOA Status Unknown", detail: "HOA involvement has not been confirmed", severity: "warning", category: "compliance" });

  // Below floor pricing
  if (quote.pricingStatus === "Below Floor")
    blockers.push({ ...base, id: `${quote.id}-floor`, label: "Below Floor Pricing", detail: "Quote price is below the pricing floor", severity: "hard", category: "financial" });
  else if (quote.pricingStatus === "Below Target")
    blockers.push({ ...base, id: `${quote.id}-target`, label: "Below Target Pricing", detail: "Quote price is below the target but above the floor", severity: "warning", category: "financial" });

  // No configuration
  if (!hasProjectConfiguration)
    blockers.push({ ...base, id: `${quote.id}-config`, label: "No Project Configuration", detail: "Project scope has not been configured", severity: "warning", category: "data" });

  return blockers;
}

/* ═══════════════════════════════════════════
   JOB BLOCKERS (enhanced from workflowBlockers)
   ═══════════════════════════════════════════ */

export function getJobBlockersUnified(job: Job, openIssues: Issue[], quotes: Quote[]): UnifiedBlocker[] {
  const blockers: UnifiedBlocker[] = [];
  const now = new Date().toISOString();
  const base = { entityType: "job" as const, entityId: job.id, source: "system" as const, createdAt: now };
  const sourceQuote = quotes.find(q => q.convertedJobId === job.id);
  const inRescission = sourceQuote?.wonAt
    ? isInRescissionFromWonAt(sourceQuote.wonAt)
    : job.rescissionStatus === "In Rescission";

  // Rescission
  if (inRescission) {
    blockers.push({ ...base, id: `${job.id}-rescission`, label: "In Rescission Period", detail: "3-day rescission period has not cleared", severity: "hard", category: "rescission" });
  }

  // Contract
  if (sourceQuote && (!sourceQuote.contract || sourceQuote.contract.status !== "Signed")) {
    blockers.push({ ...base, id: `${job.id}-contract`, label: "Contract Not Signed", detail: "Signed contract required for scheduling and ordering", severity: "hard", category: "contract", actionLink: `/opportunities/${sourceQuote.id}` });
  }

  // Permit — suppressed if permitting is bypassed
  if (!migrationBypasses(job, "permitting")) {
    if (job.permitRequired && job.permitStatus === "Revision Needed")
      blockers.push({ ...base, id: `${job.id}-permit-rev`, label: "Permit Revision Needed", detail: "City requires revised plans", severity: "hard", category: "permit", actionLink: "/permitting" });
    if (job.permitRequired && job.permitStatus === "Submitted")
      blockers.push({ ...base, id: `${job.id}-permit-pending`, label: "Awaiting Permit Approval", detail: "Permit submitted, waiting on city", severity: "warning", category: "permit", actionLink: "/permitting" });
  } else if (job.permitRequired) {
    blockers.push({ ...base, id: `${job.id}-permit-bypassed`, label: "Permitting — Bypassed", detail: "Permitting bypassed for this migrated job.", severity: "info", category: "permit" });
  }

  // Materials — suppressed if ordering is bypassed
  if (migrationBypasses(job, "ordering")) {
    blockers.push({ ...base, id: `${job.id}-mat-bypassed`, label: "Ordering — Bypassed", detail: "Ordering bypassed for this migrated job. Ordering can still allocate materials if needed.", severity: "info", category: "materials" });
  } else {
    if (job.materialReadiness === "Missing Items")
      blockers.push({ ...base, id: `${job.id}-mat-missing`, label: "Missing Materials", detail: "Materials unavailable or on backorder", severity: "hard", category: "materials", actionLink: "/warehouse" });
    if (job.materialReadiness === "Field Purchase Required")
      blockers.push({ ...base, id: `${job.id}-mat-field`, label: "Field Purchase Required", detail: "Items need to be purchased in the field", severity: "warning", category: "materials" });
    if (job.materialReadiness === "Not Ordered" && job.currentStage !== "Sale Closed" && job.currentStage !== "Review and Validation")
      blockers.push({ ...base, id: `${job.id}-mat-unordered`, label: "Materials Not Ordered", detail: "No materials order placed yet", severity: "warning", category: "materials", actionLink: "/ordering-queue" });
  }

  // Issues
  if (openIssues.length > 0) {
    const hasUrgent = openIssues.some(i => i.severity === "Urgent");
    blockers.push({ ...base, id: `${job.id}-issues`, label: `${openIssues.length} Unresolved Issue${openIssues.length > 1 ? "s" : ""}`, detail: "Open issues must be resolved", severity: hasUrgent ? "hard" : "warning", category: "issues", actionLink: `/jobs/${job.id}` });
  }

  // QA
  if (!job.qaSubmitted && (job.currentStage === "QA Review" || job.currentStage === "Completed"))
    blockers.push({ ...base, id: `${job.id}-qa`, label: "Install QA Not Submitted", detail: "QA form required before completion", severity: "hard", category: "qa" });

  // Contractor / Dispatch
  if (job.assignmentType === "Subcontractor" && !job.assignedSubcontractor && job.currentStage !== "Sale Closed" && job.currentStage !== "Review and Validation")
    blockers.push({ ...base, id: `${job.id}-sub`, label: "Subcontractor Not Assigned", detail: "No winning contractor selected", severity: "warning", category: "contractor" });
  if (job.dispatchStatus === "Rejected")
    blockers.push({ ...base, id: `${job.id}-dispatch-rej`, label: "Contractor Rejected", detail: "Assigned contractor rejected this job", severity: "hard", category: "dispatch" });
  if (job.assignmentType === "Unassigned" && !["Sale Closed", "Review and Validation", "Permit and Order Pending", "Completed"].includes(job.currentStage))
    blockers.push({ ...base, id: `${job.id}-crew`, label: "No Crew Assigned", detail: "Job needs crew or subcontractor assignment", severity: "info", category: "dispatch" });

  // Deposit / Financial
  if (sourceQuote && !sourceQuote.deposit && sourceQuote.status !== "Closed Lost")
    blockers.push({ ...base, id: `${job.id}-deposit`, label: "No Deposit Recorded", detail: "Deposit has not been collected", severity: "warning", category: "financial" });

  // HOA / POA compliance
  if (sourceQuote?.verification?.ownershipType === "POA" && !sourceQuote.verification.poaDocumentUploaded)
    blockers.push({ ...base, id: `${job.id}-poa`, label: "POA Document Missing", detail: "Power of Attorney required but not uploaded", severity: "hard", category: "compliance" });
  if (sourceQuote?.verification?.hoaInvolved === true)
    blockers.push({ ...base, id: `${job.id}-hoa`, label: "HOA Involved", detail: "HOA approval may be required before install", severity: "info", category: "compliance" });

  return blockers;
}

/* ═══════════════════════════════════════════
   SERVICE TICKET BLOCKERS
   ═══════════════════════════════════════════ */

export function getServiceTicketBlockers(ticket: ServiceTicket): UnifiedBlocker[] {
  const blockers: UnifiedBlocker[] = [];
  const now = new Date().toISOString();
  const base = { entityType: "service_ticket" as const, entityId: ticket.id, source: "system" as const, createdAt: now };

  if (!ticket.assignedTo)
    blockers.push({ ...base, id: `${ticket.id}-unassigned`, label: "Unassigned Ticket", detail: "No technician assigned to this ticket", severity: "warning", category: "dispatch" });

  if (ticket.priority === "Urgent" && ticket.status === "Open")
    blockers.push({ ...base, id: `${ticket.id}-urgent`, label: "Urgent & Unstarted", detail: "Urgent ticket has not been started", severity: "hard", category: "issues" });

  if (ticket.status === "Awaiting Parts")
    blockers.push({ ...base, id: `${ticket.id}-parts`, label: "Awaiting Parts", detail: "Parts or materials needed before resolution", severity: "warning", category: "materials" });

  if (ticket.status === "Open") {
    const daysSince = Math.floor((Date.now() - new Date(ticket.createdAt).getTime()) / 86400000);
    if (daysSince > 5)
      blockers.push({ ...base, id: `${ticket.id}-stale`, label: "Aging Ticket", detail: `Open for ${daysSince} days without progress`, severity: "warning", category: "scheduling" });
  }

  if (ticket.status === "Scheduled" && !ticket.scheduledDate)
    blockers.push({ ...base, id: `${ticket.id}-no-date`, label: "No Scheduled Date", detail: "Ticket marked scheduled but no date set", severity: "warning", category: "scheduling" });

  return blockers;
}

/* ═══════════════════════════════════════════
   CUSTOMER AGGREGATE BLOCKERS
   ═══════════════════════════════════════════ */

export function getCustomerBlockers(
  customerJobs: Job[],
  customerQuotes: Quote[],
  customerTickets: ServiceTicket[],
  allIssues: Issue[],
  allQuotes: Quote[],
): UnifiedBlocker[] {
  const blockers: UnifiedBlocker[] = [];
  for (const q of customerQuotes) blockers.push(...getQuoteBlockers(q));
  for (const j of customerJobs) {
    const jobIssues = allIssues.filter(i => i.jobId === j.id && i.status !== "Closed" && i.status !== "Resolved");
    blockers.push(...getJobBlockersUnified(j, jobIssues, allQuotes));
  }
  for (const t of customerTickets) blockers.push(...getServiceTicketBlockers(t));
  return blockers;
}

/* ═══════════════════════════════════════════
   SUMMARY HELPERS
   ═══════════════════════════════════════════ */

export function getBlockerSummary(blockers: UnifiedBlocker[]) {
  const hard = blockers.filter(b => b.severity === "hard");
  const warnings = blockers.filter(b => b.severity === "warning");
  const info = blockers.filter(b => b.severity === "info");
  const status = hard.length > 0 ? "blocked" : warnings.length > 0 ? "at_risk" : "clear";
  return { hard, warnings, info, status, total: blockers.length };
}
